import styled from 'styled-components'
import { media } from '@popety-io/ui'

export const DetailContainer = styled.div`
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  box-shadow: ${({ theme }) => theme.shadows.sm};
  padding: 32px;
  max-height: calc(100vh - 120px);
  overflow-y: auto;
  overflow-x: hidden;

  ${media.tabletDown} {
    max-height: none;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }

  ${media.mobileOnly} {
    padding: ${({ theme }) => theme.spacing.medium};
    border-radius: 0;
    border-left: none;
    border-right: none;
    max-height: calc(100vh - 60px);
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }
`

export const BackButtonContainer = styled.div`
  margin-bottom: 16px;
`

export const DetailHeader = styled.div`
  margin-bottom: 24px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 24px;

  ${media.mobileOnly} {
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing.medium};
    margin-bottom: ${({ theme }) => theme.spacing.medium};
  }
`

export const DetailMainInfo = styled.div`
  flex: 1;
`

export const DetailTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.title};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 8px 0;
`

export const DetailMeta = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.medium};
  flex-wrap: wrap;
`

export const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.text.secondary};
`

export const Divider = styled.div`
  width: 1px;
  height: 16px;
  background: ${({ theme }) => theme.colors.border.default};
`

export const ProviderBadge = styled.span<{ $color: string }>`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: ${({ theme }) => theme.typography.fontSize.micro};
  font-weight: 500;
  line-height: 1;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: ${({ $color }) => `${$color}14`};
  color: ${({ $color }) => $color};
`

export const DetailValues = styled.div`
  background: ${({ theme }) => theme.colors.background.secondary};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  padding: ${({ theme }) => theme.spacing.large};
  margin-bottom: ${({ theme }) => theme.spacing.large};
`

export const ValueRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing.medium};

  &:last-child {
    margin-bottom: 0;
  }
`

export const ValueLabel = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.text.secondary};
`

export const ValueAmount = styled.span<{ $color?: string }>`
  font-size: ${({ theme }) => theme.typography.fontSize.title};
  font-weight: 600;
  font-family: ${({ theme }) => theme.typography.fontFamily.mono};
  color: ${({ $color, theme }) => $color || theme.colors.primary};
`

export const ValueRange = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  font-family: ${({ theme }) => theme.typography.fontFamily.mono};
  color: ${({ theme }) => theme.colors.text.secondary};
`

export const DetailDescription = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: ${({ theme }) => theme.spacing.large};
`

export const SectionTitle = styled.h4`
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: ${({ theme }) => theme.spacing.medium};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
`

export const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${({ theme }) => theme.spacing.medium};
  margin-bottom: ${({ theme }) => theme.spacing.large};

  ${media.mobileOnly} {
    grid-template-columns: 1fr;
  }
`

export const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: ${({ theme }) => theme.spacing.medium};
  background: ${({ theme }) => theme.colors.background.secondary};
  border-radius: ${({ theme }) => theme.borderRadius.default};
`

export const InfoLabel = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.micro};
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${({ theme }) => theme.colors.text.tertiary};
`

export const InfoValue = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  color: ${({ theme }) => theme.colors.text.primary};
`

export const ConfidenceSection = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.large};
`

export const ConfidenceDots = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;
`

export const ConfidenceDot = styled.span<{ $filled: boolean; $color?: string }>`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${({ theme, $filled, $color }) =>
    $filled ? $color || theme.colors.primary : theme.colors.border.default};
  transition: background 0.2s ease;
`

export const ConfidenceText = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-left: ${({ theme }) => theme.spacing.small};
`
